import { useState } from "react";
import { Plus } from "@phosphor-icons/react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { TagInput, ListRow } from "./wizardShared";

export function StepCalcModules({ pack, setPack, allFieldIds }) {
  const [draft, setDraft] = useState({ id: "", name: "", description: "", inputs: [], outputs: [] });
  return (
    <div className="space-y-4 max-w-3xl" data-testid="wizard-calc">
      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm">
        <div className="px-4 py-2.5 border-b border-[var(--c-border)] text-xs font-medium text-[var(--c-text)]">Calculation Modules · {pack.calc_modules.length} <span className="text-[var(--c-muted)] font-normal">(config only — engine code ships in a later prompt)</span></div>
        {pack.calc_modules.map((m, i) => (
          <ListRow key={m.id} item={m} testid={`calc-row-${m.id}`} extra={`${m.inputs.length} inputs → ${m.outputs.length} outputs`}
            onDelete={() => setPack((p) => ({ ...p, calc_modules: p.calc_modules.filter((_, xi) => xi !== i) }))} />
        ))}
        {pack.calc_modules.length === 0 && <div className="px-4 py-6 text-xs text-[var(--c-muted)]">No modules defined.</div>}
      </div>
      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4 space-y-3">
        <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">Add module</div>
        <div className="grid grid-cols-2 gap-3">
          <Input data-testid="cm-id" value={draft.id} onChange={(e) => setDraft({ ...draft, id: e.target.value })}
            placeholder="ID · core_design" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs font-mono" />
          <Input data-testid="cm-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="Name · Core Design" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
        </div>
        <Input data-testid="cm-desc" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          placeholder="Description" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
        <div>
          <Label className="text-[10px] text-[var(--c-muted)]">Input fields (from schema)</Label>
          <div className="flex flex-wrap gap-1.5 mt-1.5 max-h-28 overflow-y-auto" data-testid="cm-inputs">
            {allFieldIds.map((fid) => {
              const on = draft.inputs.includes(fid);
              return (
                <button key={fid} type="button" data-testid={`cm-input-${fid}`}
                  onClick={() => setDraft((d) => ({ ...d, inputs: on ? d.inputs.filter((x) => x !== fid) : [...d.inputs, fid] }))}
                  className={`px-2 py-0.5 rounded-sm text-[10px] font-mono border transition-colors duration-150 ${
                    on ? "bg-[var(--c-accent)]/15 border-[var(--c-accent)] text-[var(--c-text)]" : "border-[var(--c-border)] text-[var(--c-muted)] hover:border-[var(--c-muted)]"}`}>
                  {fid}
                </button>
              );
            })}
            {allFieldIds.length === 0 && <span className="text-[11px] text-[var(--c-muted)]">Define schema fields in Step 2 first.</span>}
          </div>
        </div>
        <div>
          <Label className="text-[10px] text-[var(--c-muted)]">Output fields</Label>
          <TagInput testid="cm-outputs" tags={draft.outputs} onChange={(outputs) => setDraft({ ...draft, outputs })} placeholder="e.g., core_diameter_mm" />
        </div>
        <Button type="button" data-testid="cm-add" onClick={() => {
          if (!draft.id || !draft.name) { toast.error("Module ID and Name required"); return; }
          setPack((p) => ({ ...p, calc_modules: [...p.calc_modules, draft] }));
          setDraft({ id: "", name: "", description: "", inputs: [], outputs: [] });
        }} className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs"><Plus size={13} className="mr-1" /> Add Module</Button>
      </div>
    </div>
  );
}

export function StepValidation({ pack, setPack }) {
  const [draft, setDraft] = useState({ id: "", name: "", description: "", standards: [] });
  return (
    <div className="space-y-4 max-w-3xl" data-testid="wizard-validation">
      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm">
        <div className="px-4 py-2.5 border-b border-[var(--c-border)] text-xs font-medium text-[var(--c-text)]">Validation Domains · {pack.validation_domains.length}</div>
        {pack.validation_domains.map((d, i) => (
          <ListRow key={d.id} item={d} testid={`vdom-row-${d.id}`} extra={(d.standards || []).join(", ")}
            onDelete={() => setPack((p) => ({ ...p, validation_domains: p.validation_domains.filter((_, xi) => xi !== i) }))} />
        ))}
        {pack.validation_domains.length === 0 && <div className="px-4 py-6 text-xs text-[var(--c-muted)]">No domains defined.</div>}
      </div>
      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4 space-y-3">
        <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">Add domain</div>
        <div className="grid grid-cols-2 gap-3">
          <Input data-testid="vd-id" value={draft.id} onChange={(e) => setDraft({ ...draft, id: e.target.value })}
            placeholder="ID · electromagnetic" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs font-mono" />
          <Input data-testid="vd-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="Name · Electromagnetic" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
        </div>
        <Input data-testid="vd-desc" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          placeholder="Description" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
        <div>
          <Label className="text-[10px] text-[var(--c-muted)]">Standards</Label>
          <TagInput testid="vd-standards" tags={draft.standards} onChange={(standards) => setDraft({ ...draft, standards })} placeholder="e.g., IEC 60076-2" />
        </div>
        <Button type="button" data-testid="vd-add" onClick={() => {
          if (!draft.id || !draft.name) { toast.error("Domain ID and Name required"); return; }
          setPack((p) => ({ ...p, validation_domains: [...p.validation_domains, draft] }));
          setDraft({ id: "", name: "", description: "", standards: [] });
        }} className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs"><Plus size={13} className="mr-1" /> Add Domain</Button>
      </div>
    </div>
  );
}

export function StepDrawings({ pack, setPack }) {
  const [draft, setDraft] = useState({ id: "", name: "", mandatory: false });
  return (
    <div className="space-y-4 max-w-3xl" data-testid="wizard-drawings">
      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm">
        <div className="px-4 py-2.5 border-b border-[var(--c-border)] text-xs font-medium text-[var(--c-text)]">Drawing Types · {pack.drawing_types.length}</div>
        {pack.drawing_types.map((d, i) => (
          <ListRow key={d.id} item={d} testid={`draw-row-${d.id}`} extra={d.mandatory ? "Mandatory" : "Optional"}
            onDelete={() => setPack((p) => ({ ...p, drawing_types: p.drawing_types.filter((_, xi) => xi !== i) }))} />
        ))}
        {pack.drawing_types.length === 0 && <div className="px-4 py-6 text-xs text-[var(--c-muted)]">No drawing types defined.</div>}
      </div>
      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4 space-y-3">
        <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">Add drawing type</div>
        <div className="grid grid-cols-2 gap-3">
          <Input data-testid="dt-id" value={draft.id} onChange={(e) => setDraft({ ...draft, id: e.target.value })}
            placeholder="ID · core_assembly" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs font-mono" />
          <Input data-testid="dt-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="Name · Core Assembly" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
        </div>
        <div className="flex items-center gap-2">
          <Switch data-testid="dt-mandatory" checked={draft.mandatory} onCheckedChange={(v) => setDraft({ ...draft, mandatory: v })} />
          <Label className="text-[10px] text-[var(--c-muted)]">Mandatory drawing</Label>
        </div>
        <Button type="button" data-testid="dt-add" onClick={() => {
          if (!draft.id || !draft.name) { toast.error("Type ID and Name required"); return; }
          setPack((p) => ({ ...p, drawing_types: [...p.drawing_types, draft] }));
          setDraft({ id: "", name: "", mandatory: false });
        }} className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs"><Plus size={13} className="mr-1" /> Add Drawing Type</Button>
      </div>
    </div>
  );
}
